import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from './Button';
import { useFlexibleTranslation } from '../contexts/LanguageContext';

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const DialogContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 500px;
  max-width: 90vw;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const DialogTitle = styled.h2`
  margin: 0;
  color: #333;
  font-size: 20px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  
  &:hover {
    color: #333;
  }
`;

const Section = styled.section`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: #444;
  font-size: 16px;
  margin: 0 0 16px 0;
`;

const ConnectionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ConnectionStatus = styled.span`
  font-size: 14px;
  color: ${props => props.isConnected ? '#4caf50' : '#666'};
`;

const ConnectButton = styled(Button)`
  min-width: 100px;
`;

const Select = styled.select`
  width: 200px;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: white;
  font-size: 14px;
`;

const ThemeToggle = styled.div`
  display: flex;
  gap: 16px;
`;

const ThemeOption = styled.button`
  flex: 1;
  padding: 12px;
  border: 2px solid ${props => props.isSelected ? '#e87225' : '#e0e0e0'};
  border-radius: 8px;
  background: ${props => props.theme === 'dark' ? '#333' : 'white'};
  color: ${props => props.theme === 'dark' ? 'white' : '#333'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #e87225;
  }
`;

const WireStyleOption = styled(ThemeOption)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  svg {
    width: 40px;
    height: 24px;
  }
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  
  input {
    width: 16px;
    height: 16px;
  }
`;

const PreferencesDialog = ({ isOpen, onClose, onSave, initialPreferences }) => {
  const { t } = useFlexibleTranslation();
  const [googleConnected, setGoogleConnected] = useState(false);
  const [sharepointConnected, setSharepointConnected] = useState(false);
  const [language, setLanguage] = useState(initialPreferences?.language || 'en');
  const [theme, setTheme] = useState(initialPreferences?.theme || 'light');
  const [wireStyle, setWireStyle] = useState(initialPreferences?.wireStyle || 'solid');
  const [wireHalftone, setWireHalftone] = useState(initialPreferences?.wireHalftone || false);

  useEffect(() => {
    if (initialPreferences) {
      setLanguage(initialPreferences.language);
      setTheme(initialPreferences.theme);
      setWireStyle(initialPreferences.wireStyle);
      setWireHalftone(initialPreferences.wireHalftone);
    }
  }, [initialPreferences]);

  if (!isOpen) return null;

  const handleGoogleConnect = () => {
    // TODO: Implement Google OAuth
    setGoogleConnected(true);
  };

  const handleSharePointConnect = () => {
    // TODO: Implement SharePoint OAuth
    setSharepointConnected(true);
  };

  return (
    <DialogOverlay onClick={onClose}>
      <DialogContent onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t('preferences.title', 'Preferences')}</DialogTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </DialogHeader>

        <Section>
          <SectionTitle>{t('preferences.connections', 'Connections')}</SectionTitle>
          <ConnectionItem>
            <div>
              <svg viewBox="0 0 87.3 78" width="24" height="24" style={{ marginRight: '8px' }}>
                <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                <path d="M43.65 25l-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-27.5 47.6c-.8 1.4-1.2 2.95-1.2 4.5h27.5z" fill="#00ac47"/>
                <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l6.85 11.9z" fill="#ea4335"/>
                <path d="M43.65 25l13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                <path d="M59.8 53.5h27.5c0-1.55-.4-3.1-1.2-4.5l-27.5-47.6c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8z" fill="#2684fc"/>
                <path d="M73.4 76.8c1.6-.8 2.9-1.9 3.85-3.3l-27.5-47.6-13.75 23.8-13.75 23.8c1 1.4 2.3 2.5 3.85 3.3h47.3" fill="#ffba00"/>
              </svg>
              {t('preferences.googleDrive', 'Google Drive')}
              <ConnectionStatus isConnected={googleConnected}>
                {googleConnected ? ` • ${t('preferences.connected', 'Connected')}` : ''}
              </ConnectionStatus>
            </div>
            <ConnectButton 
              onClick={handleGoogleConnect}
              disabled={googleConnected}
              variant={googleConnected ? 'outline' : 'primary'}
            >
              {googleConnected ? t('preferences.connected', 'Connected') : t('preferences.connect', 'Connect')}
            </ConnectButton>
          </ConnectionItem>
          
          <ConnectionItem>
            <div>
              <svg viewBox="0 0 32 32" width="24" height="24" style={{ marginRight: '8px' }}>
                <path d="M19.3 10.2c-.5 0-1 .1-1.5.3l-2.4-4.1c.9-.7 1.5-1.9 1.5-3.1 0-2.2-1.8-4-4-4s-4 1.8-4 4c0 1.2.6 2.3 1.5 3.1l-2.4 4.1c-.5-.2-1-.3-1.5-.3-2.2 0-4 1.8-4 4s1.8 4 4 4c1.2 0 2.3-.6 3.1-1.5l4.1 2.4c-.2.5-.3 1-.3 1.5 0 2.2 1.8 4 4 4s4-1.8 4-4c0-1.2-.6-2.3-1.5-3.1l2.4-4.1c.5.2 1 .3 1.5.3 2.2 0 4-1.8 4-4s-1.8-4-4-4c-.5 0-1 .1-1.5.3l-2.4-4.1c.9-.7 1.5-1.9 1.5-3.1 0-2.2-1.8-4-4-4s-4 1.8-4 4z" fill="#036C70"/>
                <path d="M12.9 16c0 1.2.6 2.3 1.5 3.1l-2.4 4.1c-.5-.2-1-.3-1.5-.3-2.2 0-4 1.8-4 4s1.8 4 4 4c1.2 0 2.3-.6 3.1-1.5l4.1 2.4c-.2.5-.3 1-.3 1.5 0 2.2 1.8 4 4 4s4-1.8 4-4c0-1.2-.6-2.3-1.5-3.1l2.4-4.1c.5.2 1 .3 1.5.3 2.2 0 4-1.8 4-4s-1.8-4-4-4c-.5 0-1 .1-1.5.3l-2.4-4.1c.9-.7 1.5-1.9 1.5-3.1 0-2.2-1.8-4-4-4s-4 1.8-4 4z" fill="#1AB5BE"/>
              </svg>
              {t('preferences.sharePoint', 'SharePoint')}
              <ConnectionStatus isConnected={sharepointConnected}>
                {sharepointConnected ? ` • ${t('preferences.connected', 'Connected')}` : ''}
              </ConnectionStatus>
            </div>
            <ConnectButton
              onClick={handleSharePointConnect}
              disabled={sharepointConnected}
              variant={sharepointConnected ? 'outline' : 'primary'}
            >
              {sharepointConnected ? t('preferences.connected', 'Connected') : t('preferences.connect', 'Connect')}
            </ConnectButton>
          </ConnectionItem>
        </Section>

        <Section>
          <SectionTitle>{t('preferences.language', 'Language')}</SectionTitle>
          <Select 
            value={language} 
            onChange={e => setLanguage(e.target.value)}
          >
            <option value="en">{t('preferences.english', 'English')}</option>
            <option value="es">{t('preferences.spanish', 'Español')}</option>
            <option value="fr">{t('preferences.french', 'Français')}</option>
            <option value="de">{t('preferences.german', 'Deutsch')}</option>
            <option value="zh">{t('preferences.chinese', '中文')}</option>
          </Select>
        </Section>

        <Section>
          <SectionTitle>{t('preferences.theme', 'Theme')}</SectionTitle>
          <ThemeToggle>
            <ThemeOption 
              theme="light"
              isSelected={theme === 'light'}
              onClick={() => setTheme('light')}
            >
              {t('preferences.light', 'Light')}
            </ThemeOption>
            <ThemeOption 
              theme="dark"
              isSelected={theme === 'dark'}
              onClick={() => setTheme('dark')}
            >
              {t('preferences.dark', 'Dark')}
            </ThemeOption>
          </ThemeToggle>
        </Section>

        <Section>
          <SectionTitle>{t('preferences.wireStyle', 'Wire Style')}</SectionTitle>
          <ThemeToggle>
            <WireStyleOption
              isSelected={wireStyle === 'solid'}
              onClick={() => setWireStyle('solid')}
            >
              <svg viewBox="0 0 60 24">
                <path d="M5 12 H55" stroke="currentColor" strokeWidth="2" />
              </svg>
              {t('preferences.solid', 'Solid')}
            </WireStyleOption>
            <WireStyleOption
              isSelected={wireStyle === 'dashed'}
              onClick={() => setWireStyle('dashed')}
            >
              <svg viewBox="0 0 60 24">
                <path d="M5 12 H55" stroke="currentColor" strokeWidth="2" strokeDasharray="5,5" />
              </svg>
              {t('preferences.dashed', 'Dashed')}
            </WireStyleOption>
          </ThemeToggle>
          <div style={{ marginTop: '12px' }}>
            <Checkbox>
              <input
                type="checkbox"
                checked={wireHalftone}
                onChange={(e) => setWireHalftone(e.target.checked)}
              />
              {t('preferences.useHalftoneEffect', 'Use halftone effect for wires')}
            </Checkbox>
          </div>
        </Section>

        <Section style={{ marginTop: '24px', textAlign: 'right' }}>
          <Button 
            onClick={() => {
              onSave({ language, theme, wireStyle, wireHalftone });
              onClose();
            }}
          >
            {t('preferences.saveChanges', 'Save Changes')}
          </Button>
        </Section>
      </DialogContent>
    </DialogOverlay>
  );
};

export default PreferencesDialog; 